import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_bootstrap4.min.css';
import regionalService from '../services/regionalService';
import municipioService from '../services/municipioService';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/tipoambiente.css';
import '../styles/home.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';


const RegionalManager = () => {
  // === State ===
  const [regionais, setRegionais] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  // State for the form, shared by add and edit
  const initialState = { nome: '', descricao: '', data: '', seduc: false, municipios: [] };
  const [formData, setFormData] = useState(initialState);

  // State to track editing mode for the main form (municipalities)
  const [editingId, setEditingId] = useState(null);
  // Store original municipalities for diff calculation on update
  const [originalMunicipios, setOriginalMunicipios] = useState([]);
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);
  const formRef = useRef(null); // Reference to the form div for scrolling

  // === Callbacks & Funções ===
  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchRegionais = useCallback(async (showMessage = false) => {
    try {
      setLoading(true);
      const data = await regionalService.getAllRegionais();
      setRegionais(data || []);
      if (showMessage) mostrarMensagem('success', `${data?.length || 0} regionais carregadas.`);
      setLastUpdate(new Date());
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar regionais: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const fetchMunicipios = useCallback(async () => {
    try {
      const data = await municipioService.getAllMunicipios();
      setMunicipios(data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar municípios: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    fetchRegionais(false);
    fetchMunicipios();
  }, [fetchRegionais, fetchMunicipios]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const handleMultiSelectChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, municipios: selected }));
  };

  // Handler for INLINE ROW UPDATE (called by the Save button in "Ações" column)
  const handleRowUpdate = useCallback(async (cell) => {
    const data = cell.getRow().getData(); // Get current data from the row
    logarRequisicao('Atualizar', 'regional', { id: data.id, data });
    try {
      // Ensure date format is consistent if date was edited
      const dataToUpdate = { ...data };
      if (dataToUpdate.data && typeof dataToUpdate.data === 'string') {
        const dateObj = new Date(dataToUpdate.data);
        if (!isNaN(dateObj)) {
          dataToUpdate.data = dateObj.toISOString().split('T')[0];
        }
      }

      const response = await regionalService.updateRegional(dataToUpdate.id, dataToUpdate);
      logarSucesso('Atualizar', 'regional', response);
      mostrarMensagem('success', 'Regional atualizada com sucesso!');
      fetchRegionais(false); // Re-fetch to ensure data consistency
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'regional');
      mostrarMensagem('error', mensagemErro);
      fetchRegionais(false); // Re-fetch to revert optimistic update
    }
  }, [mostrarMensagem, fetchRegionais]);

  // Handler for the DELETE button in the "Ações" column
  const handleRowDelete = useCallback(async (cell) => {
    const data = cell.getRow().getData(); // Get current data from the row
    if (window.confirm(`Tem certeza que deseja deletar a regional "${data.nome}"?`)) {
      logarRequisicao('Excluir', 'regional', { id: data.id });
      try {
        setLoading(true);
        const response = await regionalService.deleteRegional(data.id);
        logarSucesso('Excluir', 'regional', response);
        mostrarMensagem('success', 'Regional deletada com sucesso!');
        cell.getRow().delete(); // Remove row from table directly for better UX
      } catch (error) {
        const mensagemErro = tratarErroAPI(error, 'excluir', 'regional');
        mostrarMensagem('error', mensagemErro);
      } finally {
        setLoading(false);
      }
    }
  }, [mostrarMensagem]);

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData(initialState);
    setOriginalMunicipios([]);
    setCamposComErro([]);
  };

  // Handler for the "Editar" button to load form for municipality editing
  const handleEditClick = useCallback(async (rowData) => {
    setLoading(true);
    try {
      const municipiosDaRegional = await regionalService.getMunicipiosOfRegional(rowData.id);
      const municipioIds = municipiosDaRegional.map(m => m.id_municipio.toString());

      setFormData({
        nome: rowData.nome,
        descricao: rowData.descricao,
        data: rowData.data ? new Date(rowData.data).toISOString().split('T')[0] : '', // Format date for input[type=date]
        seduc: rowData.seduc,
        municipios: municipioIds
      });
      setOriginalMunicipios(municipioIds); // Store original list for diff calculation
      setEditingId(rowData.id); // Enter edit mode for this regional
      formRef.current?.scrollIntoView({ behavior: 'smooth' }); // Scroll to the form
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar dados para edição: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  // Handler for the main form submission (Add new Regional or Update Municipalities)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!formData.nome.trim()) {
      camposObrigatorios.push('Nome');
      erros.push('nome');
    }

    if (!formData.municipios || formData.municipios.length === 0) {
      camposObrigatorios.push('Municípios Iniciais');
      erros.push('municipios');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    setLoading(true);

    try {
      if (editingId) {
        // --- LOGIC TO UPDATE MUNICIPALITY ASSOCIATIONS ONLY ---
        // Note: Main regional data (nome, descricao, data, seduc) is updated via inline table editing.
        // This form is exclusively for municipality associations when in editingId mode.
        const municipiosToAdd = formData.municipios.filter(mId => !originalMunicipios.includes(mId));
        const municipiosToRemove = originalMunicipios.filter(mId => !formData.municipios.includes(mId));

        await Promise.all([
          ...municipiosToAdd.map(mId => regionalService.addMunicipioToRegional(editingId, mId)),
          ...municipiosToRemove.map(mId => regionalService.removeMunicipioFromRegional(editingId, mId))
        ]);

        mostrarMensagem('success', 'Associações de municípios atualizadas com sucesso!');
        handleCancelEdit(); // Clear form and exit edit mode
      } else {
        // --- LOGIC TO CREATE NEW REGIONAL ---
        const regionalData = {
          nome: formData.nome,
          descricao: formData.descricao,
          data: formData.data || null,
          seduc: formData.seduc
        };
        logarRequisicao('Adicionar', 'regional', regionalData);
        const novaRegional = await regionalService.createRegional(regionalData);
        logarSucesso('Adicionar', 'regional', novaRegional);
        // Associate selected municipalities with the new regional
        await Promise.all(
          formData.municipios.map(mId => regionalService.addMunicipioToRegional(novaRegional.id, mId))
        );
        mostrarMensagem('success', 'Regional criada com sucesso!');
        setFormData(initialState); // Clear form after creation
        setCamposComErro([]);
      }
      fetchRegionais(false); // Re-fetch all regionals to update the table
    } catch (error) {
      const action = editingId ? 'atualizar associações de municípios' : 'adicionar regional';
      const mensagemErro = tratarErroAPI(error, action, 'regional');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  // Handler for the "Ver Municípios" button in the "Municípios" column
  const handleShowMunicipios = useCallback(async (cell) => {
    const rowData = cell.getRow().getData();
    try {
      setLoading(true);
      const municipiosDaRegional = await regionalService.getMunicipiosOfRegional(rowData.id);
      if (municipiosDaRegional && municipiosDaRegional.length > 0) {
        const nomes = municipiosDaRegional.map(m => m.nome_municipio).join('\n');
        alert(`Municípios da Regional "${rowData.nome}":\n\n${nomes}`);
      } else {
        alert(`A regional "${rowData.nome}" não possui municípios associados.`);
      }
    } catch (error) {
      mostrarMensagem('error', `Erro ao buscar municípios: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'regionais.csv');
    }
  };

  const getColunas = useMemo(() => ([
    {
      title: 'Ações', hozAlign: 'center', headerSort: false, width: 180,
      formatter: (cell) => {
        // Only show save button if row has changed or is new (though new rows are not added this way)
        // For simplicity, always show save and delete, it will try to save the current row state
        return `
          <button class="btn btn-sm btn-primary btn-save" title="Salvar Alterações na Linha"><i class="fas fa-save"></i></button>
          <button class="btn btn-sm btn-danger btn-delete" title="Excluir Registro"><i class="fas fa-trash"></i></button>
        `;
      },
      cellClick: (e, cell) => {
        if (e.target.closest('.btn-save')) handleRowUpdate(cell);
        if (e.target.closest('.btn-delete')) handleRowDelete(cell);
      },
    },
    { title: "Nome", field: "nome", hozAlign: "left", headerFilter: "input", tooltip: true },
    { title: "Descrição", field: "descricao", hozAlign: "left", editor: "input", headerFilter: "input", tooltip: true },
    {
      title: "Data", field: "data", hozAlign: "center", sorter: "date",
      editor: (cell, onRendered, success, cancel) => {
        const input = document.createElement("input");
        input.setAttribute("type", "text");
        input.setAttribute("placeholder", "DD/MM/YYYY");
        input.style.padding = "4px";
        input.style.width = "100%";
        input.style.boxSizing = "border-box";

        const value = cell.getValue();
        if (value) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              const day = String(date.getUTCDate()).padStart(2, '0');
              const month = String(date.getUTCMonth() + 1).padStart(2, '0');
              const year = date.getUTCFullYear();
              input.value = `${day}/${month}/${year}`;
            } else {
              input.value = value;
            }
          } catch (e) {
            input.value = value;
          }
        }

        onRendered(() => {
          input.focus();
          input.style.height = "100%";
        });

        const handleDateInput = (e) => {
          const input = e.target;
          let value = input.value.replace(/\D/g, '');
          let formattedValue = '';
          if (value.length > 0) {
            formattedValue = value.substring(0, 2);
          }
          if (value.length > 2) {
            formattedValue += '/' + value.substring(2, 4);
          }
          if (value.length > 4) {
            formattedValue += '/' + value.substring(4, 8);
          }
          input.value = formattedValue;
        };

        input.addEventListener('input', handleDateInput);

        const onChange = () => {
          if (input.value && input.value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)) {
            const [day, month, year] = input.value.split('/');
            success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
          } else {
            success(input.value);
          }
        };

        input.addEventListener("change", onChange);
        input.addEventListener("blur", onChange);

        return input;
      },
      formatter: (cell) => {
        const value = cell.getValue();
        if (!value) return 'N/A';
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) return 'N/A';
          const day = String(date.getUTCDate()).padStart(2, '0');
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const year = date.getUTCFullYear();
          return `${day}/${month}/${year}`;
        } catch (e) {
          return 'N/A';
        }
      }
    },
    { title: "Seduc", field: "seduc", hozAlign: "center", formatter: "tickCross", width: 100, editor: true },
    {
      title: 'Municípios', hozAlign: 'center', headerSort: false, width: 250,
      formatter: (cell) => {
        return `
          <button class="btn btn-sm btn-primary btn-edit-mun" title="Editar Associações"><i class="fas fa-edit"></i> Editar</button>
          <button class="btn btn-sm btn-info btn-ver" title="Ver Municípios"><i class="fas fa-eye"></i> Ver</button>
        `;
      },
      cellClick: (e, cell) => {
        const rowData = cell.getRow().getData();
        if (e.target.closest('.btn-edit-mun')) handleEditClick(rowData);
        if (e.target.closest('.btn-ver')) handleShowMunicipios(cell);
      },
    },
  ]), [handleRowUpdate, handleRowDelete, handleShowMunicipios, handleEditClick]);

  useEffect(() => {
    if (tableRef.current && regionais) {
      if (tabulatorRef.current) tabulatorRef.current.destroy();
      tabulatorRef.current = new Tabulator(tableRef.current, {
        data: regionais,
        columns: getColunas,
        layout: "fitColumns", // Changed from fitDataStretch for better column width distribution
        pagination: "local", paginationSize: 10,
        movableColumns: true,
        tooltips: true,
        placeholder: "Nenhuma regional encontrada",
        index: 'id'
      });
    }
  }, [regionais, getColunas]);

  // === JSX ===
  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()} title="Voltar"><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Regionais</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/regional.mov" buttonText="Ver Tutorial Regional" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls" ref={formRef}>
        <h2 className="form-title">{editingId ? `Editando Associações para: ${formData.nome}` : 'Adicionar Nova Regional'}</h2>
        <form onSubmit={handleSubmit} className="form-inline">
          {/* Fields for main regional data (disabled when editing associations) */}
          <div className="form-group"><label>Nome:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label><input type="text" name="nome" value={formData.nome} onChange={handleInputChange} placeholder="Nome da regional" required disabled={loading || editingId} className={camposComErro.includes('nome') ? 'input-error' : ''} /></div>
          <div className="form-group"><label>Descrição:</label><textarea name="descricao" value={formData.descricao} onChange={handleInputChange} placeholder="Descrição opcional" disabled={loading || editingId} /></div>
          <div className="form-group"><label>Data:</label><input type="date" name="data" value={formData.data} onChange={handleInputChange} disabled={loading || editingId} /></div>
          <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center' }}><label htmlFor="seduc-add" style={{ marginRight: '10px' }}>Seduc:</label><input type="checkbox" id="seduc-add" name="seduc" checked={formData.seduc} onChange={handleInputChange} disabled={loading || editingId} /></div>

          {/* Municipality Multi-select (always enabled as it's the focus of edit mode) */}
          <div className="form-group form-group-full-width">
            <label htmlFor="municipios-add">
              {editingId ? `Gerenciar municípios de ${formData.nome}` : 'Municípios Iniciais'} (Segure Ctrl/Cmd para múltiplos):<span style={{ color: 'red', marginLeft: '2px' }}>*</span>
            </label>
            <select id="municipios-add" name="municipios" value={formData.municipios} onChange={handleMultiSelectChange} multiple style={{ height: '150px' }} disabled={loading} className={camposComErro.includes('municipios') ? 'input-error' : ''}>
              {municipios.map(m => (<option key={m.id_municipio} value={m.id_municipio}>{m.nome_municipio}</option>))}
            </select>
          </div>

          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading}>
                {editingId
                  ? <><i className="fas fa-save"></i> Salvar associações</>
                  : <><i className="fas fa-plus"></i> Adicionar nova regional</>
                }
              </button>
              {editingId && (
                <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} disabled={loading}>
                  <i className="fas fa-times"></i> Cancelar edição
                </button>
              )}
              <button type="button" className="btn btn-primary" onClick={() => fetchRegionais(true)} disabled={loading}>
                {loading ? 'Recarregando...' : <><i className="fas fa-sync-alt"></i> Recarregar tabela</>}
              </button>
              <button type="button" className="btn btn-secondary" onClick={exportarDados} disabled={regionais.length === 0}>
                <i className="fas fa-file-export"></i> Exportar CSV
              </button>
            </div>

            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <span><strong>{regionais.length}</strong> registros</span>
        <span>Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></span>
      </div>

      <div className="tabulator-container"><div ref={tableRef}></div></div>
    </div>
  );
};

export default RegionalManager;